import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';

interface SiteContentContextValue {
  values: Record<string, string>;
  getValue: (key: string, fallback?: string) => string;
  refresh: () => Promise<void>;
}

const SiteContentContext = createContext<SiteContentContextValue>({
  values: {},
  getValue: (_key: string, fallback = '') => fallback,
  refresh: async () => {},
});

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [values, setValues] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('site_content').select('key, value');
      if (!error && data) {
        const map: Record<string, string> = {};
        data.forEach((row) => {
          map[row.key] = row.value ?? '';
        });
        setValues(map);
      }
    } catch {
      // Si falla la carga, se mantienen los valores por defecto de los componentes.
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refrescar automáticamente cuando el usuario vuelve a la pestaña
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refresh]);

  const getValue = useCallback(
    (key: string, fallback = '') => {
      const value = values[key];
      return value ? value : fallback;
    },
    [values],
  );

  return (
    <SiteContentContext.Provider value={{ values, getValue, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}